import type { LoaderArgs, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import type { ShouldRevalidateFunction } from '@remix-run/react';
import { Outlet, useLoaderData } from '@remix-run/react';
import Debug from 'debug';
import invariant from 'tiny-invariant';
import RelatedProducts from '~/components/Home/RelatedProducts';
import Breadcrumb from '~/components/shared/Breadcrumb';
import { PricesBreakdownEstimates, RELATED_PRODUCTS_LIMIT, scApi } from '~/services/api.server';
import { getUpdatedAuthentication } from '~/services/auth.server';
import { setNotification } from '~/services/cookies.server';
import { commitSession, getSession } from '~/services/session.server';
import { isErrorResponse, isPackProduct } from '~/utils/common';

export type ProductSlugLoaderData = Awaited<ReturnType<Awaited<ReturnType<typeof loader>>['json']>>;

const debug = Debug('sc:mango:route:shop:$productSlug');

export const shouldRevalidate: ShouldRevalidateFunction = ({ currentParams, nextParams }) =>
  currentParams.productSlug !== nextParams.productSlug;

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const url = new URL(data.url);
  const productType = isPackProduct(data.product) ? 'Pack' : 'Product';
  const { title, description, image, keywords } = {
    title: `SwagCharm Shop | Swag ${productType} | ${data.product.name}`,
    description: `Swag ${productType} | ${data.product.name} | ${data.product.description}`,
    image: `https://${url.host}/api/image?src=${data.product.image}&width=600&height=600`,
    keywords: `custom swag, high quality swag, swag, custom t-shirts, custom hoodies, custom hats, custom stickers, ${data.product.name}`,
  };
  return {
    title,
    description,
    image,
    keywords,
    'og:title': title,
    'og:type': 'website',
    'og:description': description,
    'og:image': image,
    'twitter:card': 'summary_large_image',
  };
};

export async function loader({ request, params }: LoaderArgs) {
  debug('url, referer:', request.url, request.headers.get('referer'));
  const { productSlug } = params;
  invariant(productSlug, 'product slug is required');

  const { user } = await getUpdatedAuthentication(request);
  const session = await getSession(request);

  const accountId = session.get('accountId');
  const product = await scApi.getProduct(user?.accessToken, accountId, productSlug, {
    expand: 'supplier_inventories,selected_mockup',
  });

  if (isErrorResponse(product)) {
    if (product.error_code === '404') {
      setNotification(session, 'error', 'Product not found');
    }
    return redirect('/shop', { headers: { 'Set-Cookie': await commitSession(session) } });
  }

  // TODO: Related products should be deferred
  const [relatedProducts, pricesBreakdownRes, pricesBreakdownBlankRes] = await Promise.all([
    scApi.getRelatedProducts(user?.accessToken, accountId, {
      category: product.category.id.toString(),
      exclude: product.id.toString(),
      limit: RELATED_PRODUCTS_LIMIT.toString(),
    }),
    scApi.getPricesBreakdownEstimates(user?.accessToken, accountId, product.id, {
      quantities: [10, 50, 100, 250],
      blank: false,
    }),
    scApi.getPricesBreakdownEstimates(user?.accessToken, accountId, product.id, {
      quantities: [10, 50, 100, 250],
      blank: true,
    }),
  ]);

  const defaultBreakdown = {
    currency: 'USD',
    prices: [],
  } satisfies PricesBreakdownEstimates;

  const pricesBreakdown = isErrorResponse(pricesBreakdownRes) ? defaultBreakdown : pricesBreakdownRes;
  const pricesBreakdownBlank = isErrorResponse(pricesBreakdownBlankRes) ? defaultBreakdown : pricesBreakdownBlankRes;

  return json({
    product,
    relatedProducts,
    url: request.url,
    pricesBreakdown,
    pricesBreakdownBlank,
    isAuthenticated: user !== null,
  });
}

export default function ProductLoader() {
  const { product, relatedProducts } = useLoaderData<typeof loader>();
  const breadcrumbEntries = [
    { name: 'Home', href: '/' },
    { name: 'Shop', href: `/shop/${isPackProduct(product) ? 'swag-packs' : 'bulk-swag'}` },
    { name: product.name || '', href: '/shop/' + product.slug },
  ];

  return (
    <>
      <Breadcrumb entries={breadcrumbEntries} />
      <Outlet />
      <RelatedProducts products={relatedProducts.results} />
    </>
  );
}
